import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AdCard } from "./Advertise";
import {
    ArrowRight,
    MessageSquare,
    PhoneCall,
    Phone,
    CreditCard,
    Sparkles,
    Calendar,
    AlertTriangle,
    Signal,
} from "lucide-react";

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [numbers, setNumbers] = useState([]);
    const [threads, setThreads] = useState([]);
    const [calls, setCalls] = useState([]);
    const [sponsoredAd, setSponsoredAd] = useState(null);
    const [wireless, setWireless] = useState(null);
    const [wirelessUsage, setWirelessUsage] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [s, n, t, c] = await Promise.all([
                    api.get("/stats"),
                    api.get("/numbers/my"),
                    api.get("/sms/threads"),
                    api.get("/calls"),
                ]);
                setStats(s.data);
                setNumbers(n.data);
                setThreads(t.data.slice(0, 5));
                setCalls(c.data.slice(0, 5));
            } catch (e) {
                console.error(e);
            }
            try {
                const { data } = await api.get("/mobile/subscription");
                const active = (data.subscriptions || []).find((x) => x.status === "active");
                if (active) {
                    setWireless(active);
                    try {
                        const u = await api.get(`/mobile/subscription/${active.mobile_sub_id}/usage`);
                        setWirelessUsage(u.data.usage);
                    } catch { /* usage non-critical */ }
                }
            } catch { /* wireless non-critical */ }
            try {
                const { data } = await api.get("/ads/active");
                if (data.length) setSponsoredAd(data[Math.floor(Math.random() * data.length)]);
            } catch { /* ads are non-critical */ }
        })();
    }, []);

    return (
        <div className="space-y-8" data-testid="dashboard-root">
            <div className="flex items-end justify-between gap-6">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                        — My Account
                    </p>
                    <h1 className="font-display text-4xl font-bold tracking-tight">
                        Welcome back, {user?.name?.split(" ")[0]}.
                    </h1>
                </div>
                <button
                    data-testid="dashboard-buy-number-btn"
                    onClick={() => navigate("/numbers")}
                    className="h-10 px-4 bg-espresso text-[#FAF5FF] rounded-sm text-sm flex items-center gap-2 hover:bg-black"
                >
                    Get a number <ArrowRight className="w-4 h-4" />
                </button>
            </div>

            {user?.signup_credit_cents > 0 && (
                <div className="border border-emerald-deep bg-[#ECFDF5] rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="account-credit-banner">
                    <div>
                        <p className="font-medium text-emerald-deep">
                            You have a ${(user.signup_credit_cents / 100).toFixed(2)} account credit
                        </p>
                        <p className="text-sm text-zinc-600 mt-1">
                            From your earlier number purchase. Pick your number and your first month of Lite is just{" "}
                            <span className="font-medium">${(7.99 - user.signup_credit_cents / 100).toFixed(2)}</span> — your
                            credit is applied automatically at checkout.
                        </p>
                    </div>
                    <button
                        data-testid="account-credit-claim-btn"
                        onClick={() => navigate("/numbers?plan=lite&cycle=monthly")}
                        className="h-10 px-5 bg-emerald-deep hover:bg-blue-600 text-white rounded-sm text-sm flex items-center gap-2 shrink-0"
                    >
                        Claim my number <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {user?.plan && user.plan !== "free" && numbers.length === 0 && !user?.is_admin && (
                <div className="border border-[#D97706] bg-[#FEF3C7] rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="no-number-banner">
                    <div>
                        <p className="font-medium text-[#D97706]">Your plan is active — but you don&apos;t have a phone number yet</p>
                        <p className="text-sm text-zinc-600 mt-1">
                            Your subscription includes a number. Claim it now at no extra charge.
                        </p>
                    </div>
                    <button
                        data-testid="dashboard-pick-number-btn"
                        onClick={() => navigate("/numbers")}
                        className="h-10 px-5 bg-[#D97706] hover:bg-[#B45309] text-white rounded-sm text-sm flex items-center gap-2 shrink-0"
                    >
                        Claim my number <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {user?.plan && user.plan !== "free" && user?.plan_expires_at && (
                <PlanExpiryBanner
                    plan={user.plan}
                    billingCycle={user.billing_cycle}
                    expiresAt={user.plan_expires_at}
                    onRenew={() => navigate("/billing")}
                />
            )}

            {wireless && <WirelessCard sub={wireless} usage={wirelessUsage} navigate={navigate} />}

            <div className="border border-[#DDD6FE] bg-white rounded-sm p-6" data-testid="native-calling-card">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-full bg-brand-green/10 flex items-center justify-center shrink-0">
                            <Signal className="w-5 h-5 text-brand-green" />
                        </div>
                        <div>
                            <h3 className="font-medium text-espresso">Calls &amp; texts work right on your phone</h3>
                            <p className="text-sm text-zinc-500 mt-1 max-w-xl">
                                Your FlexLine number works natively in your phone&apos;s own dialer and messages — no app to open.
                                Just install your eSIM and go. Bring your existing number and it becomes your line for calls in and out.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <button data-testid="native-activate-esim-btn" onClick={() => navigate("/mobile")}
                            className="h-10 px-4 bg-brand-green text-white rounded-sm text-sm flex items-center gap-2 hover:opacity-90">
                            Activate eSIM <ArrowRight className="w-4 h-4" />
                        </button>
                        <button data-testid="native-bring-number-btn" onClick={() => navigate("/porting")}
                            className="h-10 px-4 border border-espresso/30 text-espresso rounded-sm text-sm hover:bg-cream">
                            Bring my number
                        </button>
                    </div>
                </div>
                <p className="text-xs text-zinc-400 mt-4">
                    Traveling or between phones? You can also make calls from the app under <button onClick={() => navigate("/calls")} className="underline hover:text-espresso" data-testid="backup-calling-link">Backup calling</button>.
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                <Stat label="Active lines" value={stats?.numbers ?? 0} icon={Phone} testid="stat-numbers" />
                <Stat label="Messages" value={stats?.messages ?? 0} icon={MessageSquare} testid="stat-messages" />
                <Stat label="Calls" value={stats?.calls ?? 0} icon={PhoneCall} testid="stat-calls" />
                <Stat label="SMS credits" value={stats?.credits ?? 0} icon={CreditCard} testid="stat-credits" highlight />
            </div>

            {sponsoredAd && (
                <AdCard ad={sponsoredAd} />
            )}

            {numbers.length === 0 && (
                <div className="border border-[#DDD6FE] rounded-sm bg-paper p-10 text-center" data-testid="empty-state-numbers">
                    <Sparkles className="w-6 h-6 mx-auto text-accent-blue mb-3" />
                    <h3 className="font-display text-2xl font-bold">Pick your first number</h3>
                    <p className="text-zinc-600 mt-2 max-w-md mx-auto">
                        You don&apos;t have any active numbers yet. Pick one in seconds and start receiving SMS.
                    </p>
                    <button
                        data-testid="empty-buy-btn"
                        onClick={() => navigate("/numbers")}
                        className="mt-6 h-10 px-5 bg-espresso text-[#FAF5FF] rounded-sm text-sm inline-flex items-center gap-2"
                    >
                        Browse the catalog <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
                <Panel title="Active numbers" cta={{ label: "Manage", to: "/my-numbers" }}>
                    {numbers.length === 0 && <Empty label="No numbers yet" />}
                    <ul className="divide-y divide-[#DDD6FE]/60">
                        {numbers.slice(0, 5).map((n) => (
                            <li key={n.number_id} className="py-3 flex items-center justify-between" data-testid={`number-row-${n.number_id}`}>
                                <div>
                                    <div className="font-mono text-sm">{n.phone_number}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        {n.locality || n.region || n.country} · {n.capabilities.join(" · ")}
                                        {n.demo && <span className="ml-2 px-1.5 py-0.5 bg-[#E4DAF7] text-[#4C1D95] text-[10px] font-mono rounded-sm">DEMO</span>}
                                    </div>
                                </div>
                                <span className="font-mono text-[10px] text-emerald-deep uppercase">Active</span>
                            </li>
                        ))}
                    </ul>
                </Panel>

                <Panel title="Recent conversations" cta={{ label: "Open inbox", to: "/inbox" }}>
                    {threads.length === 0 && <Empty label="No messages yet" />}
                    <ul className="divide-y divide-[#DDD6FE]/60">
                        {threads.map((t) => (
                            <li key={t.thread_id} className="py-3 flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                    <div className="font-mono text-sm">{t.peer_number}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{t.last_message}</div>
                                </div>
                                <span className="font-mono text-[10px] text-zinc-400">{relative(t.last_at)}</span>
                            </li>
                        ))}
                    </ul>
                </Panel>

                <Panel title="Recent calls" cta={{ label: "All calls", to: "/calls" }} className="lg:col-span-2">
                    {calls.length === 0 && <Empty label="No calls yet" />}
                    <ul className="divide-y divide-[#DDD6FE]/60">
                        {calls.map((c) => (
                            <li key={c.call_id} className="py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span
                                        className={`w-2 h-2 rounded-full ${
                                            c.direction === "inbound" ? "bg-emerald-deep" : "bg-zinc-400"
                                        }`}
                                    />
                                    <div>
                                        <div className="font-mono text-sm">
                                            {c.direction === "inbound" ? c.from_number : c.to_number}
                                        </div>
                                        <div className="text-xs text-zinc-500 mt-0.5">
                                            {c.direction} · {c.status} · {Math.floor(c.duration_seconds / 60)}m {c.duration_seconds % 60}s
                                        </div>
                                    </div>
                                </div>
                                <span className="font-mono text-[10px] text-zinc-400">{relative(c.created_at)}</span>
                            </li>
                        ))}
                    </ul>
                </Panel>
            </div>
        </div>
    );
}

function WirelessCard({ sub, usage, navigate }) {
    const esim = sub.esim || {};
    const pct = usage?.percent;
    return (
        <div className="border border-emerald-deep/40 rounded-sm bg-gradient-to-br from-[#ECFDF5] to-paper p-6" data-testid="dashboard-wireless-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-1">FlexLine Mobile · Wireless line</p>
                    <div className="flex items-center gap-3">
                        <span className="font-display text-3xl font-light tracking-tight" data-testid="wireless-number">{sub.phone_number || "—"}</span>
                        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase text-emerald-deep">
                            <span className="w-2 h-2 rounded-full bg-emerald-deep animate-pulse" /> {esim.status === "ready" ? "Connected" : (esim.status || "Active")}
                        </span>
                    </div>
                    <p className="text-sm text-zinc-600 mt-1">{sub.plan_name} · ${sub.plan_price}/mo · {sub.data}</p>
                </div>
            </div>
            {usage && (
                <div className="mt-5 max-w-md">
                    <div className="flex items-baseline justify-between text-sm mb-1.5">
                        <span className="text-zinc-500">Data used</span>
                        <span className="font-medium" data-testid="wireless-usage">{usage.consumed_gb} GB {usage.unlimited ? "· Unlimited" : `of ${usage.limit_gb} GB`}</span>
                    </div>
                    {!usage.unlimited && (
                        <div className="h-2 rounded-full bg-[#D6ECE2] overflow-hidden">
                            <div className={`h-full rounded-full ${pct >= 90 ? "bg-[#D97706]" : "bg-emerald-deep"}`} style={{ width: `${pct || 0}%` }} />
                        </div>
                    )}
                </div>
            )}
            <div className="mt-6 flex flex-wrap gap-2">
                <ActionBtn label="Manage eSIM" onClick={() => navigate("/mobile")} testid="wireless-manage-esim" primary />
                <ActionBtn label="Upgrade plan" onClick={() => navigate("/mobile")} testid="wireless-upgrade" />
                <ActionBtn label="Shop" onClick={() => navigate("/shop")} testid="wireless-shop" />
                <ActionBtn label="Pay bill" onClick={() => navigate("/billing")} testid="wireless-pay-bill" />
                <ActionBtn label="Support" onClick={() => navigate("/support")} testid="wireless-support" />
            </div>
        </div>
    );
}

function ActionBtn({ label, onClick, testid, primary }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`h-9 px-4 rounded-sm text-sm font-medium ${primary ? "bg-emerald-deep text-white hover:bg-emerald-deep/90" : "border border-[#DDD6FE] bg-paper hover:bg-cream"}`}>
            {label}
        </button>
    );
}

function Stat({ label, value, icon: Icon, highlight, testid }) {
    return (
        <div className={`bg-paper p-6 ${highlight ? "" : ""}`} data-testid={testid}>
            <div className="flex items-start justify-between">
                <p className="font-mono text-[11px] uppercase text-zinc-500">{label}</p>
                <Icon className={`w-4 h-4 ${highlight ? "text-emerald-deep" : "text-zinc-400"}`} />
            </div>
            <div className="font-display text-5xl font-light mt-3 tracking-tight">{value}</div>
        </div>
    );
}

function Panel({ title, cta, children, className = "" }) {
    return (
        <div className={`border border-[#DDD6FE] rounded-sm bg-paper p-6 ${className}`}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-mono text-[11px] font-medium uppercase tracking-wider text-zinc-500">{title}</h3>
                {cta && (
                    <a href={cta.to} className="text-xs text-emerald-deep font-mono link-underline">
                        {cta.label} →
                    </a>
                )}
            </div>
            {children}
        </div>
    );
}

function Empty({ label }) {
    return <div className="py-8 text-center text-sm text-zinc-500">{label}</div>;
}

function relative(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}

function PlanExpiryBanner({ plan, billingCycle, expiresAt, onRenew }) {
    const exp = new Date(expiresAt);
    const daysLeft = Math.max(0, Math.ceil((exp.getTime() - Date.now()) / 86400000));
    const expiring = daysLeft <= 14;
    const dateStr = exp.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
    return (
        <div
            data-testid="plan-expiry-banner"
            className={`border rounded-sm p-5 flex items-center justify-between gap-4 ${
                expiring
                    ? "bg-[#F3E8FF] border-[#C4B5FD]"
                    : "bg-paper border-[#DDD6FE]"
            }`}
        >
            <div className="flex items-center gap-4">
                {expiring ? (
                    <AlertTriangle className="w-5 h-5 text-[#6D28D9]" />
                ) : (
                    <Calendar className="w-5 h-5 text-emerald-deep" />
                )}
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        Current plan · {billingCycle || "monthly"}
                    </div>
                    <div className="text-sm mt-0.5">
                        <span className="font-display text-lg capitalize tracking-tight">{plan}</span>
                        <span className="text-zinc-500"> · renews </span>
                        <span className="font-mono">{dateStr}</span>
                        <span className={`ml-2 font-mono text-xs ${expiring ? "text-[#6D28D9]" : "text-emerald-deep"}`}>
                            ({daysLeft} {daysLeft === 1 ? "day" : "days"} left)
                        </span>
                    </div>
                </div>
            </div>
            <button
                onClick={onRenew}
                data-testid="plan-renew-btn"
                className={`h-9 px-4 rounded-sm text-sm font-medium ${
                    expiring
                        ? "bg-[#6D28D9] text-white hover:bg-[#6D28D9]"
                        : "bg-espresso text-[#FAF5FF] hover:bg-black"
                }`}
            >
                {expiring ? "Renew now" : "Manage"}
            </button>
        </div>
    );
}
